import {
  formatDateShort,
  formatDateTimeShort,
  isDateFieldType,
} from '@ardoq/date-time';
import {
  Answers,
  AttributeAnswer,
  AttributeAnswerValue,
  DateRange,
  FieldAnswer,
  FieldAnswerValueWithoutDateRange,
  Fields,
  ReferenceAnswer,
  ParentAnswer,
  ReferenceAnswerValue,
  FieldAnswerValue,
} from '../types';
import {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  PersistedAttributeQuestion,
  PersistedConditionFieldQuestion,
  PersistedFieldQuestion,
  PersistedFieldSubQuestion,
  PersistedParentQuestion,
  PersistedReferenceQuestion,
  PersistedReferenceSubQuestion,
  PersistedSurveyQuestion,
  SurveyQuestionValidator,
  UnsetDisplayCondition,
  ValidDisplayConditions,
} from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';
import {
  getDateRangeStartAndEndDateFields,
  getFieldType,
} from '../utils/fieldOperations';
import {
  containsSubQuestions,
  isParentQuestion,
  isRangeValidator,
} from './questionPredicates';
import { isDateRangeFieldType } from '@ardoq/date-range';
import {
  getAttributeQuestionAnswer,
  getDateRangeFieldQuestionAnswer,
  getFieldQuestionAnswer,
  getParsedConditionAnswer,
} from '../utils/answerOperations';
import { isValueBooleanOrEmpty } from '../utils/stringUtils';

const getIdentifierValue = (
  value: string | number | boolean | null | undefined,
  type: APIFieldType,
  locale: Locale
) => {
  if (
    (type === APIFieldType.CHECKBOX && isValueBooleanOrEmpty(value)) ||
    value === true
  ) {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return value;
  }
  if (!value) {
    return 'No value';
  }
  if (isDateFieldType(type) && typeof value === 'string') {
    return formatDateShort(value, locale);
  }
  return value;
};

export const getIdentifierStringFromComponentValue = (
  value: string | number | boolean | null | undefined,
  fields: Fields,
  identifierFieldName: string | undefined,
  locale: Locale
) => {
  if (!identifierFieldName) {
    return undefined;
  }
  const field = fields[identifierFieldName];
  if (!field) {
    return undefined;
  }
  return `[${field.label}: ${getIdentifierValue(value, field.type, locale)}]`;
};

export const getIdentifierStringFromReferenceAnswerValue = (
  ref: ReferenceAnswerValue,
  fields: Fields,
  identifierFieldName: string | undefined,
  locale: Locale
): string => {
  if (!identifierFieldName) {
    return '';
  }
  const field = fields[identifierFieldName];
  if (!field) {
    return '';
  }

  // Newly created references will not have identifierValue from the API
  // but rather the identifierString created in the front end
  if (ref.component.identifierString) {
    return ref.component.identifierString;
  }

  // a newly created component will not have an identifier value
  // but if the field has a defaultValue, we should show that
  const value =
    ref.tempId && !ref.component.identifierValue
      ? (field.defaultValue as string | number | boolean | null | undefined)
      : ref.component.identifierValue;

  return `[${field.label}: ${getIdentifierValue(value, field.type, locale)}]`;
};

type SetupAttributeQuestionDataArgs = {
  question: PersistedParentQuestion | PersistedAttributeQuestion;
  answers: Answers;
  isSaving: boolean;
  defaultParentId: ArdoqId | undefined | null;
};

export type AttributeQuestionData = {
  originalValue?: AttributeAnswerValue;
  validateDuplicateName: boolean;
  answer: AttributeAnswer | ParentAnswer | undefined;
  value: string | null | undefined;
};

export const setupAttributeQuestionData = ({
  question,
  answers,
  isSaving,
  defaultParentId,
}: SetupAttributeQuestionDataArgs): AttributeQuestionData => {
  const answer = getAttributeQuestionAnswer(question._id, answers);
  const originalValue = answer ? answer.originalValue : undefined;
  return {
    originalValue,
    validateDuplicateName: !isSaving,
    answer,
    value: getAttributeValue({ question, answer, defaultParentId }),
  };
};

type GetAttributeValueArgs = {
  question: PersistedParentQuestion | PersistedAttributeQuestion;
  answer?: AttributeAnswer | ParentAnswer;
  defaultParentId?: ArdoqId | null;
};

const getAttributeValue = ({
  question,
  answer,
  defaultParentId,
}: GetAttributeValueArgs) => {
  if (answer?.value !== undefined) return answer.value;
  if (isParentQuestion(question)) return defaultParentId;
};

export type FieldQuestionData = {
  type: APIFieldType | undefined;
  field: APIFieldAttributes | undefined;
  answer: FieldAnswer<FieldAnswerValueWithoutDateRange> | undefined;
  originalValue: FieldAnswerValueWithoutDateRange | undefined;
  validateDuplicateName: boolean;
  value: FieldAnswerValueWithoutDateRange | undefined;
};

type SetupFieldQuestionDataArgs = {
  fields: Fields;
  question: PersistedFieldQuestion;
  answers: Answers;
  isSaving: boolean;
};

export const setupFieldQuestionData = ({
  fields,
  question,
  answers,
  isSaving,
}: SetupFieldQuestionDataArgs): FieldQuestionData => {
  const fieldName = question.properties.fieldName;
  const field = fields[fieldName];
  const answer = getFieldQuestionAnswer(
    question._id,
    answers
  ) as FieldAnswer<FieldAnswerValueWithoutDateRange>;
  const type = getFieldType(fieldName, fields);

  return {
    type,
    field,
    answer,
    originalValue: answer ? answer.originalValue : undefined,
    validateDuplicateName: !isSaving,
    value: getFieldQuestionValue({
      answer,
      type,
      field,
    }),
  };
};

export const getFormatter = (isDateOnlyRange: boolean) =>
  isDateOnlyRange ? formatDateShort : formatDateTimeShort;

type GetFieldQuestionValueArgs = {
  answer: FieldAnswer<FieldAnswerValueWithoutDateRange> | undefined;
  type?: APIFieldType;
  field?: APIFieldAttributes;
};

const getFieldQuestionValue = ({
  answer,
  type,
  field,
}: GetFieldQuestionValueArgs) => {
  if (answer?.value !== undefined) return answer.value;
  if (
    !type ||
    [APIFieldType.LIST, APIFieldType.SELECT_MULTIPLE_LIST].includes(type)
  ) {
    return undefined;
  }
  if (type === APIFieldType.NUMBER) {
    return null;
  }
  return field?.defaultValue as string | number | boolean | null | undefined;
};

type GetDateRangeValueArgs = {
  answer?: FieldAnswer<DateRange | undefined>;
  type?: APIFieldType.DATE_ONLY_RANGE | APIFieldType.DATE_TIME_RANGE;
};

const getDateRangeValue = ({ answer, type }: GetDateRangeValueArgs) => {
  if (answer?.value !== undefined) return answer.value;
  if (!type) return undefined;
};

type SetupDateRangeFieldQuestionDataArgs = {
  fields: Fields;
  question: PersistedFieldQuestion;
  answers: Answers;
};

export type DateRangeFieldQuestionData = {
  answer: FieldAnswer<DateRange> | undefined;
  startDate: APIFieldAttributes | undefined;
  endDate: APIFieldAttributes | undefined;
  value: DateRange | undefined;
};

export const setupDateRangeFieldQuestionData = ({
  fields,
  question,
  answers,
}: SetupDateRangeFieldQuestionDataArgs): DateRangeFieldQuestionData => {
  const fieldName = question.properties.fieldName;
  const answer = getDateRangeFieldQuestionAnswer(question._id, answers);
  const fieldType = getFieldType(fieldName, fields);
  const type = isDateRangeFieldType(fieldType) ? fieldType : undefined;
  const startAndEndDates = getDateRangeStartAndEndDateFields(question, fields);
  return {
    answer,
    startDate: startAndEndDates?.startDate,
    endDate: startAndEndDates?.endDate,
    value: getDateRangeValue({
      answer,
      type,
    }),
  };
};

export const checkShouldFetchReferenceSuggestions = (
  hasReferenceSuggestionsFeature: boolean,
  aiSuggestionsEnabled?: boolean
) => Boolean(hasReferenceSuggestionsFeature && aiSuggestionsEnabled);

export const getRangeValidator = (validators?: SurveyQuestionValidator[]) =>
  validators && validators.find(isRangeValidator);

export const getNoAccessReferenceCount = (
  answer: ReferenceAnswer | undefined
) => {
  return Math.abs(
    (answer?.originalValue?.length ?? 0) - (answer?.totalReferences ?? 0)
  );
};

export const noAccessReferencesMeetMaxRangeValidation = (
  noAccessReferences: number,
  validators: SurveyQuestionValidator[] | undefined
): boolean => {
  const rangeValidator = getRangeValidator(validators);
  return Boolean(
    rangeValidator?.max && noAccessReferences >= rangeValidator.max
  );
};

const isUnsetCondition = (
  condition: ValidDisplayConditions
): condition is UnsetDisplayCondition =>
  condition && (condition as UnsetDisplayCondition).type === 'unset';

export const displayConditionsIncludesAnswer = (
  parsedParentAnswer: any,
  parentFieldType?: APIFieldType,
  displayConditions?: ValidDisplayConditions[]
) => {
  switch (parentFieldType) {
    case APIFieldType.CHECKBOX:
      return displayConditions?.some(condition => {
        const compareValue = isUnsetCondition(condition)
          ? undefined
          : condition.value;
        return compareValue === parsedParentAnswer;
      });
    case APIFieldType.LIST:
      return displayConditions?.some(condition => {
        const compareValue = isUnsetCondition(condition)
          ? null
          : condition.value;
        return compareValue === parsedParentAnswer;
      });
    default:
      return false;
  }
};

export const getRelevantConditionalQuestions = (
  questions: PersistedFieldSubQuestion[] | undefined,
  conditionAnswer: FieldAnswerValue | undefined,
  conditionFieldType: APIFieldType | undefined
) =>
  questions?.filter(question => {
    const displayConditions = question.properties.displayConditions;
    const parsedConditionAnswer = getParsedConditionAnswer(
      conditionAnswer,
      conditionFieldType
    );
    return displayConditionsIncludesAnswer(
      parsedConditionAnswer,
      conditionFieldType,
      displayConditions
    );
  });

export function getSubQuestions(
  question: PersistedConditionFieldQuestion
): PersistedFieldSubQuestion[];
export function getSubQuestions(
  question: PersistedReferenceQuestion
): PersistedReferenceSubQuestion[];
export function getSubQuestions(
  question: PersistedSurveyQuestion
): PersistedSurveyQuestion[];
export function getSubQuestions(
  question: PersistedSurveyQuestion
): PersistedSurveyQuestion[] {
  if (containsSubQuestions(question) && question.properties.questions) {
    return question.properties.questions;
  }
  return [];
}
